import { useCallback, useEffect, useState } from "react";

import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import type { Commodity } from "~/shared/schemas/commodity-schema.ts";

import { useDeleteSavedCommodity } from "./use-commodities.ts";

type UseCommodityDeletionProps<T extends Commodity = Commodity> = {
	setData: React.Dispatch<React.SetStateAction<T[]>>;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	resetOn?: boolean;
	totalItems?: number;
	skip?: number;
	take?: number;
	setSkip?: React.Dispatch<React.SetStateAction<number>>;
};

export function useCommodityDeletion<T extends Commodity = Commodity>({
	setData,
	setIsLoading,
	resetOn = false,
	totalItems = 0,
	skip = 0,
	take = 25,
	setSkip,
}: UseCommodityDeletionProps<T>) {
	const deleteSavedCommodityMutation = useDeleteSavedCommodity();
	const { showSuccess, showError } = useNotifications();

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [itemToDelete, setItemToDelete] = useState<T | null>(null);

	useEffect(() => {
		if (resetOn) {
			setShowConfirmation(false);
			setItemToDelete(null);
		}
	}, [resetOn]);

	const handleRemoveFromList = useCallback((dataItem: T) => {
		setItemToDelete(dataItem);
		setShowConfirmation(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (!itemToDelete) return;

		const pendingDeletion = itemToDelete;
		const resolveDisplayName = () => {
			const trimmedDescription = pendingDeletion.description?.trim();
			return trimmedDescription && trimmedDescription.length > 0
				? trimmedDescription
				: "Commodity";
		};

		setShowConfirmation(false);
		setIsLoading(true);

		deleteSavedCommodityMutation.mutate(pendingDeletion.id, {
			onSuccess: () => {
				setData((prev) => {
					const updatedData = prev.filter(
						(item) => item.id !== pendingDeletion.id,
					);
					const newTotalItems = updatedData.length;

					if (
						setSkip &&
						totalItems > 0 &&
						skip >= newTotalItems &&
						newTotalItems > 0
					) {
						const maxValidSkip = Math.max(
							0,
							Math.floor((newTotalItems - 1) / take) * take,
						);
						setSkip(maxValidSkip);
					}

					return updatedData;
				});
				setIsLoading(false);
				showSuccess(`${resolveDisplayName()} removed from saved commodities.`);
				setItemToDelete(null);
			},
			onError: (err: Error) => {
				setIsLoading(false);
				showError(
					err.message ??
						`Unable to remove ${resolveDisplayName()}. Please try again shortly.`,
				);
			},
		});
	}, [
		itemToDelete,
		deleteSavedCommodityMutation,
		setData,
		setIsLoading,
		showSuccess,
		showError,
		setSkip,
		totalItems,
		skip,
		take,
	]);

	const handleCancelDelete = useCallback(() => {
		setShowConfirmation(false);
		setItemToDelete(null);
	}, []);

	return {
		showConfirmation,
		itemToDelete,
		handleRemoveFromList,
		handleConfirmDelete,
		handleCancelDelete,
	};
}
